import React, { useEffect, useMemo, useRef, useState } from 'react';

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const WEEK_W = 52;
const WEEK_GAP = 2;

function toIsoDate(d) { return d.toISOString().split('T')[0]; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function mondayOf(dateIso) {
    const d = new Date(dateIso + 'T00:00:00');
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + diff);
    return toIsoDate(d);
}
function isoWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Horizontal week scroller (months + weeks rows) with coverage dots.
 * Props:
 *  - coverageDates: array of ISO date strings that have data
 *  - minDate / maxDate: ISO bounds of available data (drives the weeks range)
 *  - startDate / endDate: current selection
 *  - onChange(startIso, endIso)
 *  - scrollTo: 'latest' (default) → scroll to the most recent week on mount
 */
export default function WeekScroller({ coverageDates = [], minDate, maxDate, startDate, endDate, onChange }) {
    const scrollerRef = useRef(null);
    const [anchor, setAnchor] = useState(null);

    const weeks = useMemo(() => {
        const lower = minDate || toIsoDate(new Date());
        const start = new Date(mondayOf(lower) + 'T00:00:00');
        const upperIso = maxDate && maxDate > toIsoDate(new Date()) ? maxDate : toIsoDate(new Date());
        const end = addDays(new Date(mondayOf(upperIso) + 'T00:00:00'), 7);
        const ws = [];
        let cur = new Date(start);
        while (cur <= end) { ws.push(new Date(cur)); cur.setDate(cur.getDate() + 7); }
        return ws;
    }, [minDate, maxDate]);

    const monthGroups = useMemo(() => {
        const groups = [];
        let current = null;
        for (let i = 0; i < weeks.length; i++) {
            const thu = addDays(weeks[i], 3);
            const key = `${thu.getFullYear()}-${thu.getMonth() + 1}`;
            if (!current || current.key !== key) {
                current = { key, year: thu.getFullYear(), month: thu.getMonth() + 1, nWeeks: 1 };
                groups.push(current);
            } else {
                current.nWeeks += 1;
            }
        }
        return groups;
    }, [weeks]);

    const coverageMap = useMemo(() => {
        const m = {};
        for (const d of coverageDates) {
            const mon = mondayOf(d);
            if (!m[mon]) m[mon] = new Set();
            m[mon].add(d);
        }
        return m;
    }, [coverageDates]);

    // Scroll to the latest week by default
    useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.scrollLeft = scrollerRef.current.scrollWidth;
        }
    }, [weeks]);

    const selectedMon = startDate ? mondayOf(startDate) : null;
    const selectedEndMon = endDate ? mondayOf(endDate) : null;
    const isSelected = (monIso) => selectedMon && selectedEndMon && monIso >= selectedMon && monIso <= selectedEndMon;

    const selectedMonthKey = useMemo(() => {
        if (!startDate || !endDate || startDate.slice(8, 10) !== '01') return null;
        const [y, m] = startDate.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        if (endDate !== `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`) return null;
        return `${y}-${m}`;
    }, [startDate, endDate]);

    const handleWeekClick = (mon, e) => {
        const monIso = toIsoDate(mon);
        const sunIso = toIsoDate(addDays(mon, 6));
        if (e.shiftKey && anchor) {
            const lo = anchor < monIso ? anchor : monIso;
            const hi = anchor < monIso ? monIso : anchor;
            onChange(lo, toIsoDate(addDays(new Date(hi + 'T00:00:00'), 6)));
        } else {
            setAnchor(monIso);
            onChange(monIso, sunIso);
        }
    };

    const handleMonthClick = (year, month) => {
        const lastDay = new Date(year, month, 0).getDate();
        onChange(
            `${year}-${String(month).padStart(2, '0')}-01`,
            `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
        );
    };

    const goPrev = () => { if (scrollerRef.current) scrollerRef.current.scrollLeft -= 200; };
    const goNext = () => { if (scrollerRef.current) scrollerRef.current.scrollLeft += 200; };

    const todayMon = mondayOf(toIsoDate(new Date()));

    return (
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 p-1" data-testid="vitrine-week-scroller">
            <button type="button" onClick={goPrev} className="h-14 w-6 flex-shrink-0 text-slate-400 hover:text-slate-700" title="Précédent">‹</button>
            <div ref={scrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                <div className="min-w-max">
                    <div className="flex" style={{ gap: `${WEEK_GAP}px` }}>
                        {monthGroups.map((g) => {
                            const width = g.nWeeks * WEEK_W + (g.nWeeks - 1) * WEEK_GAP;
                            const isActive = selectedMonthKey === g.key;
                            return (
                                <button
                                    key={g.key}
                                    type="button"
                                    onClick={() => handleMonthClick(g.year, g.month)}
                                    className={`shrink-0 h-5 mb-0.5 rounded border text-[10px] font-semibold leading-none ${isActive ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                    style={{ width: `${width}px` }}
                                    data-testid={`vitrine-month-${g.year}-${g.month}`}
                                >
                                    {MONTHS_FR[g.month - 1]}
                                    {g.month === 1 && <span className="ml-1 opacity-50">{g.year}</span>}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex" style={{ gap: `${WEEK_GAP}px` }}>
                        {weeks.map((mon, i) => {
                            const monIso = toIsoDate(mon);
                            const count = coverageMap[monIso]?.size || 0;
                            const selected = isSelected(monIso);
                            const isCur = monIso === todayMon;
                            const dotClass = count >= 7 ? 'bg-emerald-500' : count > 0 ? 'bg-amber-400' : 'bg-slate-300';
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={(e) => handleWeekClick(mon, e)}
                                    title={`Semaine ${isoWeekNumber(mon)} — ${monIso} → ${toIsoDate(addDays(mon, 6))}${count > 0 ? ` (${count}/7 jours)` : ''}\nShift+clic = plage`}
                                    className={`shrink-0 py-1 rounded-md border text-[10px] leading-tight text-center ${selected ? 'bg-slate-900 text-white border-slate-900' : isCur ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                                    style={{ width: `${WEEK_W}px` }}
                                    data-testid={`vitrine-week-${monIso}`}
                                >
                                    <div className="font-bold">S{isoWeekNumber(mon)}</div>
                                    <div className={`text-[10px] font-medium ${selected ? 'text-slate-200' : 'text-slate-600'}`}>
                                        {String(mon.getDate()).padStart(2, '0')}/{String(mon.getMonth() + 1).padStart(2, '0')}
                                    </div>
                                    <div className="flex justify-center mt-0.5">
                                        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <button type="button" onClick={goNext} className="h-14 w-6 flex-shrink-0 text-slate-400 hover:text-slate-700" title="Suivant">›</button>
        </div>
    );
}
